import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton } from '@material-ui/core'
import { ArrowUpward, ArrowDownward, Delete, Add } from '@material-ui/icons'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../commonDesign/Button'
import Switch from '../../../../../../../molecules/Switch'

const modeOptions = [
  { label: 'Membership (group hierarchy)', value: 'membership' },
  { label: 'Purpose (switch data purpose)', value: 'purpose' }
]

const displayOptions = [
  { label: 'Simple (picker only)', value: 'simple' },
  { label: 'Complex (type switcher + picker)', value: 'complex' }
]

const sizeOptions = [
  { label: 'Small', value: 'sm' },
  { label: 'Large', value: 'lg' }
]

function ScopeItem ({ scope, index, total, onUpdate, onRemove, onMoveUp, onMoveDown, scopeTypeOptions }) {
  const handleScopeTypeChange = useCallback((scopeType) => {
    const updated = { ...scope, scopeType }
    if (!scope.displayName) {
      updated.displayName = scopeType
    }
    if (!scope.labels?.allItems) {
      updated.labels = { ...(scope.labels || {}), allItems: `All ${scopeType}s` }
    }
    onUpdate(updated)
  }, [scope, onUpdate])

  return (
    <div style={{
      border: '1px solid var(--border-color)',
      borderRadius: 'var(--border-radius-sm)',
      padding: '12px',
      marginBottom: '8px'
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span className='font-size-sm' style={{ fontWeight: 600 }}>
          {scope.displayName || scope.scopeType || '(unnamed)'}
        </span>
        <div style={{ display: 'flex', gap: '2px' }}>
          <IconButton size='small' disabled={index === 0} onClick={onMoveUp}>
            <ArrowUpward fontSize='small' />
          </IconButton>
          <IconButton size='small' disabled={index === total - 1} onClick={onMoveDown}>
            <ArrowDownward fontSize='small' />
          </IconButton>
          <IconButton size='small' onClick={onRemove}>
            <Delete fontSize='small' />
          </IconButton>
        </div>
      </div>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <SydLabel label='Scope Type' required>
            <SydSelect
              options={scopeTypeOptions}
              value={scope.scopeType || '__placeholder__'}
              onChange={(e) => {
                const val = e.target.value
                if (val !== '__placeholder__') handleScopeTypeChange(val)
              }}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Display Name' helperText='Auto-filled from scope type; editable'>
            <SydInput
              value={scope.displayName || ''}
              onChange={(e) => onUpdate({ ...scope, displayName: e.target.value || undefined })}
              placeholder='e.g., By Advisor'
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Icon'>
            <SydInput
              value={scope.icon || ''}
              onChange={(e) => onUpdate({ ...scope, icon: e.target.value || null })}
              placeholder='Icon name'
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='All Items Label' helperText='Auto-filled from scope type; editable'>
            <SydInput
              value={scope.labels?.allItems || ''}
              onChange={(e) => onUpdate({
                ...scope,
                labels: { ...(scope.labels || {}), allItems: e.target.value }
              })}
              placeholder='e.g., All Advisors'
            />
          </SydLabel>
        </Grid>
      </Grid>
    </div>
  )
}

ScopeItem.propTypes = {
  scope: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  scopeTypeOptions: PropTypes.array.isRequired
}

function PurposeItem ({ purpose, index, total, onUpdate, onRemove, onMoveUp, onMoveDown, allPurposes }) {
  const purposeOptions = useMemo(() => {
    const entries = Object.entries(allPurposes || {})
    return [
      { label: 'Select a purpose...', value: '__placeholder__' },
      ...entries.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  return (
    <div style={{
      border: '1px solid var(--border-color)',
      borderRadius: 'var(--border-radius-sm)',
      padding: '12px',
      marginBottom: '8px'
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span className='font-size-sm' style={{ fontWeight: 600 }}>
          {purpose.displayName || purpose.purposeCode || '(unnamed)'}
        </span>
        <div style={{ display: 'flex', gap: '2px' }}>
          <IconButton size='small' disabled={index === 0} onClick={onMoveUp}>
            <ArrowUpward fontSize='small' />
          </IconButton>
          <IconButton size='small' disabled={index === total - 1} onClick={onMoveDown}>
            <ArrowDownward fontSize='small' />
          </IconButton>
          <IconButton size='small' onClick={onRemove}>
            <Delete fontSize='small' />
          </IconButton>
        </div>
      </div>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <SydLabel label='Purpose Code' required>
            <SydSelect
              options={purposeOptions}
              value={purpose.purposeCode || '__placeholder__'}
              onChange={(e) => onUpdate({
                ...purpose,
                purposeCode: e.target.value === '__placeholder__' ? null : e.target.value
              })}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Display Name' required>
            <SydInput
              value={purpose.displayName || ''}
              onChange={(e) => onUpdate({ ...purpose, displayName: e.target.value })}
              placeholder='e.g., Advisor View'
            />
          </SydLabel>
        </Grid>
      </Grid>
    </div>
  )
}

PurposeItem.propTypes = {
  purpose: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  allPurposes: PropTypes.object
}

function ScopeSelectorConfigEditor ({ config, allPurposes, onUpdate }) {
  const [localConfig, setLocalConfig] = useState(config)

  useEffect(() => {
    if (JSON.stringify(localConfig) !== JSON.stringify(config)) {
      onUpdate(localConfig)
    }
  }, [localConfig, config, onUpdate])

  const purposeOptions = useMemo(() => {
    const entries = Object.entries(allPurposes || {})
    return [
      { label: 'Select a purpose...', value: '__placeholder__' },
      ...entries.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const membershipPredicates = useMemo(() => {
    if (localConfig.mode !== 'membership' || !localConfig.purposeCode) return []
    const purposeData = allPurposes?.[localConfig.purposeCode]
    return (purposeData?.scopePredicates || [])
      .filter(sp => sp.type === 'membership')
  }, [localConfig.mode, localConfig.purposeCode, allPurposes])

  const allScopeTypes = useMemo(() => membershipPredicates.map(sp => sp.scopeType), [membershipPredicates])

  const usedScopeTypes = useMemo(() => {
    return new Set((localConfig.scopes || []).map(s => s.scopeType).filter(Boolean))
  }, [localConfig.scopes])

  const unusedScopeTypes = useMemo(() => {
    return allScopeTypes.filter(st => !usedScopeTypes.has(st))
  }, [allScopeTypes, usedScopeTypes])

  const handleModeChange = useCallback((mode) => {
    setLocalConfig(prev => {
      const base = {
        ...prev,
        mode,
        display: prev.display || 'simple',
        multiSelect: prev.multiSelect ?? false,
        hideAvatars: prev.hideAvatars ?? false,
        size: prev.size || 'sm'
      }
      if (mode === 'membership') {
        const { purposes: _p, ...rest } = base
        return { ...rest, purposeCode: prev.purposeCode || null, scopes: prev.scopes || [] }
      }
      const { purposeCode: _pc, scopes: _s, ...rest } = base
      return { ...rest, purposes: prev.purposes || [] }
    })
  }, [])

  const handleScopeUpdate = useCallback((index, updated) => {
    setLocalConfig(prev => {
      const scopes = [...(prev.scopes || [])]
      scopes[index] = updated
      return { ...prev, scopes }
    })
  }, [])

  const handleScopeRemove = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      scopes: (prev.scopes || []).filter((_, i) => i !== index)
    }))
  }, [])

  const handleScopeMove = useCallback((index, direction) => {
    setLocalConfig(prev => {
      const scopes = [...(prev.scopes || [])]
      const target = index + direction
      if (target < 0 || target >= scopes.length) return prev
      ;[scopes[index], scopes[target]] = [scopes[target], scopes[index]]
      return { ...prev, scopes }
    })
  }, [])

  const handleAddScope = useCallback((scopeType) => {
    setLocalConfig(prev => ({
      ...prev,
      scopes: [...(prev.scopes || []), {
        scopeType,
        displayName: scopeType,
        labels: { allItems: `All ${scopeType}s` }
      }]
    }))
  }, [])

  const handlePurposeItemUpdate = useCallback((index, updated) => {
    setLocalConfig(prev => {
      const purposes = [...(prev.purposes || [])]
      purposes[index] = updated
      return { ...prev, purposes }
    })
  }, [])

  const handlePurposeItemRemove = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      purposes: (prev.purposes || []).filter((_, i) => i !== index)
    }))
  }, [])

  const handlePurposeItemMove = useCallback((index, direction) => {
    setLocalConfig(prev => {
      const purposes = [...(prev.purposes || [])]
      const target = index + direction
      if (target < 0 || target >= purposes.length) return prev
      ;[purposes[index], purposes[target]] = [purposes[target], purposes[index]]
      return { ...prev, purposes }
    })
  }, [])

  const handleAddPurposeItem = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      purposes: [...(prev.purposes || []), { purposeCode: null, displayName: '' }]
    }))
  }, [])

  const mode = localConfig.mode || 'membership'

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SydLabel required label='Mode' helperText='How scope options are derived'>
          <SydSelect
            options={modeOptions}
            value={mode}
            onChange={(e) => handleModeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {mode === 'membership' && (
        <>
          <Grid item xs={12}>
            <SydLabel required label='Source Purpose' helperText='Purpose whose scopePredicates define available scopes'>
              <SydSelect
                options={purposeOptions}
                value={localConfig.purposeCode || '__placeholder__'}
                onChange={(e) => setLocalConfig(prev => ({
                  ...prev,
                  purposeCode: e.target.value === '__placeholder__' ? null : e.target.value
                }))}
              />
            </SydLabel>
          </Grid>

          <Grid item xs={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <h4 style={{ margin: 0 }}>Scopes</h4>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
                Configure which scopes to show and in what order. Leave empty to auto-derive from all membership predicates.
              </span>
            </div>
            {(localConfig.scopes || []).map((scope, i) => {
              const otherUsed = new Set(
                (localConfig.scopes || [])
                  .filter((_, j) => j !== i)
                  .map(s => s.scopeType)
                  .filter(Boolean)
              )
              const options = [
                { label: 'Select scope type…', value: '__placeholder__' },
                ...allScopeTypes
                  .filter(st => !otherUsed.has(st))
                  .map(st => ({ label: st, value: st }))
              ]
              return (
                <ScopeItem
                  key={i}
                  scope={scope}
                  index={i}
                  total={(localConfig.scopes || []).length}
                  scopeTypeOptions={options}
                  onUpdate={(updated) => handleScopeUpdate(i, updated)}
                  onRemove={() => handleScopeRemove(i)}
                  onMoveUp={() => handleScopeMove(i, -1)}
                  onMoveDown={() => handleScopeMove(i, 1)}
                />
              )
            })}
            {unusedScopeTypes.length > 0
              ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SydSelect
                    options={[
                      { label: 'Add scope…', value: '__placeholder__' },
                      ...unusedScopeTypes.map(st => ({ label: st, value: st }))
                    ]}
                    value='__placeholder__'
                    onChange={(e) => {
                      const val = e.target.value
                      if (val !== '__placeholder__') handleAddScope(val)
                    }}
                    size='sm'
                  />
                </div>
              )
              : allScopeTypes.length > 0
                ? (
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
                    All available scope types have been added.
                  </span>
                )
                : null}
          </Grid>
        </>
      )}

      {mode === 'purpose' && (
        <Grid item xs={12}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <h4 style={{ margin: 0 }}>Purposes</h4>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
              Each purpose becomes a selectable scope option.
            </span>
          </div>
          {(localConfig.purposes || []).map((purpose, i) => (
            <PurposeItem
              key={i}
              purpose={purpose}
              index={i}
              total={(localConfig.purposes || []).length}
              allPurposes={allPurposes}
              onUpdate={(updated) => handlePurposeItemUpdate(i, updated)}
              onRemove={() => handlePurposeItemRemove(i)}
              onMoveUp={() => handlePurposeItemMove(i, -1)}
              onMoveDown={() => handlePurposeItemMove(i, 1)}
            />
          ))}
          <SydButton size='sm' variant='outline' icon={<Add />} onClick={handleAddPurposeItem}>
            Add Purpose
          </SydButton>
        </Grid>
      )}

      <Grid item xs={12}>
        <h4 style={{ margin: '0 0 8px 0' }}>Display Settings</h4>
      </Grid>

      <Grid item xs={6} md={4}>
        <SydLabel label='Display Mode' helperText='Simple shows picker only; Complex adds scope type switcher'>
          <SydSelect
            options={displayOptions}
            value={localConfig.display || 'simple'}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, display: e.target.value }))}
          />
        </SydLabel>
      </Grid>

      <Grid item xs={6} md={4}>
        <SydLabel label='Size'>
          <SydSelect
            options={sizeOptions}
            value={localConfig.size || 'sm'}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, size: e.target.value }))}
          />
        </SydLabel>
      </Grid>

      <Grid item xs={6} md={4}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Switch
              checked={localConfig.multiSelect ?? false}
              onChange={(e) => setLocalConfig(prev => ({ ...prev, multiSelect: e.target.checked }))}
            />
            <span className='font-size-sm'>Multi-select</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Switch
              checked={localConfig.hideAvatars ?? false}
              onChange={(e) => setLocalConfig(prev => ({ ...prev, hideAvatars: e.target.checked }))}
            />
            <span className='font-size-sm'>Hide avatars</span>
          </div>
        </div>
      </Grid>
    </Grid>
  )
}

ScopeSelectorConfigEditor.propTypes = {
  config: PropTypes.object.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default ScopeSelectorConfigEditor
